import { configManager } from "@/features/client-config/services/config-manager";
import { networkMonitor } from "@/services/monitoring/network-monitor/network-monitor";
import { captureException } from "@/utils/sentry";

export type WebSocketMessage = {
  type: string;
  data: unknown;
  timestamp: number;
};

export type WebSocketEvent = {
  type: "connected" | "disconnected" | "message" | "error";
  message?: WebSocketMessage;
  error?: Error;
};

export type WebSocketConnectionState =
  | "connecting"
  | "open"
  | "closing"
  | "closed";

/**
 * WebSocket Client with Infinite Reconnection
 *
 * One instance per radar connection — each instance owns its own socket,
 * reconnect/backoff state, and heartbeat, so one radar's disconnect never
 * affects another's connection.
 *
 * Features:
 * - Never gives up on reconnection (infinite retries)
 * - Exponential backoff (1s → 2s → 4s → 8s → 16s → 32s → 60s max)
 * - Network-aware: auto-reconnects when network comes back online
 * - Message queuing: queues messages when disconnected
 *
 * This is critical for long-running PWA apps that should work 24/7
 * even if backend restarts or network drops temporarily.
 */
export class WebSocketClient {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private reconnectDelay = 1000; // Start at 1 second
  private maxReconnectDelay = 60000; // Cap at 60 seconds
  private messageQueue: WebSocketMessage[] = [];
  private listeners: ((event: WebSocketEvent) => void)[] = [];
  private isConnecting = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private shouldBeConnected = false; // Track if we want to be connected
  private clientId: string | null = null; // Store client ID for messages
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null; // Heartbeat timer
  private heartbeatTimeout: ReturnType<typeof setTimeout> | null = null; // Heartbeat response timeout
  private lastPongTime = 0; // Track last pong received
  private readonly HEARTBEAT_INTERVAL = 30000; // Send ping every 30 seconds
  private readonly HEARTBEAT_TIMEOUT = 10000; // Wait 10 seconds for pong response
  private readonly MIN_RECONNECT_DELAY = 500; // Minimum delay before first reconnect (ms)
  private hasHadSuccessfulConnection = false; // Track if we've ever connected successfully
  private networkMonitorUnsubscribe: (() => void) | null = null;
  private readonly radarId: string;
  private url: string;

  constructor(radarId: string, url: string) {
    this.radarId = radarId;
    this.url = url;
    this.setupNetworkMonitoring();
  }

  /**
   * Update the target URL (e.g. when the radar's config changes). Does not
   * itself trigger a reconnect — callers that need one should follow up
   * with disconnect()/connect() or rely on the next reconnect attempt.
   */
  setUrl(url: string): void {
    this.url = url;
  }

  /**
   * Setup network monitoring to auto-reconnect when network comes back
   */
  private setupNetworkMonitoring(): void {
    this.networkMonitorUnsubscribe = networkMonitor.addListener((event) => {
      if (event.type === "online" && this.shouldBeConnected) {
        console.log(
          `🌐 [WebSocket:${this.radarId}] Network back online - attempting reconnect`
        );
        // Reset reconnect attempts when network comes back
        this.reconnectAttempts = 0;
        // Cancel any pending reconnect timer
        if (this.reconnectTimer) {
          clearTimeout(this.reconnectTimer);
          this.reconnectTimer = null;
        }
        // Try to connect immediately
        this.connect();
      }

      if (event.type === "offline") {
        console.log(
          `🌐 [WebSocket:${this.radarId}] Network offline - will retry when online`
        );
      }
    });
  }

  async connect(): Promise<void> {
    // If already connected, no need to reconnect
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log(`✅ [WebSocket:${this.radarId}] Already connected`);
      return;
    }

    // If already attempting to connect, don't start another connection
    if (this.isConnecting) {
      console.log(
        `⏳ [WebSocket:${this.radarId}] Connection already in progress`
      );
      return;
    }

    if (!this.url) {
      console.log(
        `⚠️ [WebSocket:${this.radarId}] Cannot connect - no URL configured`
      );
      return;
    }

    // Get client ID from storage
    if (!this.clientId) {
      const clientConfig = await configManager.getConfig();
      this.clientId = clientConfig?.clientId || null;
      console.log(
        `🔌 [WebSocket:${this.radarId}] Client ID from storage:`,
        this.clientId
      );
    }

    this.shouldBeConnected = true; // Mark that we want to be connected
    this.isConnecting = true;

    // Cancel any pending reconnect timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // Clean up any existing WebSocket that's in CLOSING or CLOSED state
    if (this.ws) {
      const state = this.ws.readyState;
      if (state === WebSocket.CLOSING || state === WebSocket.CLOSED) {
        console.log(
          `🧹 [WebSocket:${this.radarId}] Cleaning up old WebSocket instance`
        );
        this.ws = null;
      }
    }

    try {
      console.log(
        `🔌 [WebSocket:${this.radarId}] Connecting to ${this.url}... (attempt ${this.reconnectAttempts + 1})`
      );
      this.ws = new WebSocket(this.url);

      this.ws.onopen = () => {
        this.isConnecting = false;
        this.reconnectAttempts = 0; // Reset on successful connection
        this.hasHadSuccessfulConnection = true; // Mark that we've connected at least once
        console.log(`✅ [WebSocket:${this.radarId}] Connected`);

        // Subscribe to receive shot data
        console.log(
          `🔌 [WebSocket:${this.radarId}] Subscribing to shot data (ALL messages)`
        );
        this.sendServerMessage("Subscribe", { MessageList: ["ALL"] });

        // Start heartbeat monitoring
        this.startHeartbeat();

        this.notifyListeners({ type: "connected" });
        this.processMessageQueue();
      };

      this.ws.onmessage = (event) => {
        try {
          // Parse the raw JSON from the server
          const rawData = JSON.parse(event.data);

          // Check for ping/pong messages (handle both formats)
          const isPing =
            rawData.type === "ping" ||
            rawData.type === "Ping" ||
            rawData.Type === "Ping";

          const isPong =
            rawData.type === "pong" ||
            rawData.type === "Pong" ||
            rawData.Type === "Pong";

          // Handle incoming ping messages from server (respond with pong).
          // Not logged: fires every heartbeat interval on a healthy
          // connection, so it's noise rather than information.
          if (isPing) {
            // Send pong in server's expected format: {"Type": "Pong", "Payload": null}
            this.sendServerMessage("Pong", null);
            // Update lastPongTime to indicate we're alive
            this.lastPongTime = Date.now();
            return;
          }

          // Handle heartbeat responses (pong from our own pings). Not
          // logged for the same reason as the ping branch above.
          if (isPong) {
            this.lastPongTime = Date.now();
            return;
          }

          // Pass the raw data directly as the message
          // This preserves the server's format (Type, SubType, Payload)
          this.notifyListeners({
            type: "message",
            message: rawData as unknown as WebSocketMessage,
          });
        } catch (error) {
          console.error(
            `Failed to parse WebSocket message [${this.radarId}]:`,
            error
          );
        }
      };

      this.ws.onclose = (event) => {
        this.isConnecting = false;
        console.log(`❌ [WebSocket:${this.radarId}] Disconnected`, {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
        });

        // Stop heartbeat monitoring
        this.stopHeartbeat();

        // Clean up the WebSocket reference
        this.ws = null;

        this.notifyListeners({ type: "disconnected" });

        // Only reconnect if we want to be connected
        if (this.shouldBeConnected) {
          console.log(
            `🔄 [WebSocket:${this.radarId}] Connection lost, initiating immediate reconnect...`
          );
          this.attemptReconnect();
        }
      };

      this.ws.onerror = (error) => {
        this.isConnecting = false;
        console.error(`❌ [WebSocket:${this.radarId}] Error occurred:`, error);

        // Stop heartbeat monitoring on error
        this.stopHeartbeat();

        this.notifyListeners({
          type: "error",
          error: new Error("WebSocket error"),
        });

        // If we're still wanting to be connected and we're not already closing/closed,
        // the onclose handler will trigger reconnection. But if the socket is in a bad state,
        // we might need to force cleanup and reconnect
        if (this.shouldBeConnected && this.ws) {
          const state = this.ws.readyState;
          // If socket is stuck in CONNECTING state, force close to trigger onclose
          if (state === WebSocket.CONNECTING) {
            console.warn(
              `⚠️ [WebSocket:${this.radarId}] Stuck in CONNECTING state, forcing close`
            );
            try {
              this.ws.close();
            } catch (e) {
              console.error(
                `Error forcing WebSocket close [${this.radarId}]:`,
                e
              );
              // Manually trigger reconnection if close fails
              this.ws = null;
              this.attemptReconnect();
            }
          }
        }
      };
    } catch (error) {
      this.isConnecting = false;
      console.error(`Failed to create WebSocket [${this.radarId}]:`, error);
      this.notifyListeners({ type: "error", error: error as Error });

      // Retry even if creation fails
      if (this.shouldBeConnected) {
        this.attemptReconnect();
      }
    }
  }

  /**
   * Attempt to reconnect with exponential backoff
   * Never gives up - critical for long-running PWA apps
   *
   * Strategy:
   * - First reconnect after disconnection: 500ms (fast recovery for temporary issues)
   * - Subsequent attempts: exponential backoff (1s, 2s, 4s, 8s, 16s, 32s, 60s max)
   * - Continues forever until reconnected or manually disconnected
   */
  private async attemptReconnect(): Promise<void> {
    // Cancel any existing timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (!this.url) {
      console.log(
        `⚠️ [WebSocket:${this.radarId}] Skipping reconnect - no URL configured`
      );
      this.shouldBeConnected = false;
      return;
    }

    this.reconnectAttempts++;

    // Calculate delay with exponential backoff
    let delay: number;

    if (this.reconnectAttempts === 1 && this.hasHadSuccessfulConnection) {
      // If this is the first reconnect attempt after we've been connected before,
      // use a shorter delay (500ms) for fast recovery from temporary issues
      delay = this.MIN_RECONNECT_DELAY;
      console.log(
        `🔄 [WebSocket:${this.radarId}] Connection lost - attempting immediate reconnect in ${delay}ms...`
      );
    } else {
      // Use exponential backoff: 1s, 2s, 4s, 8s, 16s, 32s, 60s (capped)
      delay = Math.min(
        this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
        this.maxReconnectDelay
      );
      console.log(
        `🔄 [WebSocket:${this.radarId}] Reconnection attempt ${this.reconnectAttempts} scheduled in ${delay / 1000}s`
      );
    }

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      console.log(
        `🔄 [WebSocket:${this.radarId}] Executing reconnection attempt ${this.reconnectAttempts}...`
      );
      this.connect();
    }, delay);
  }

  private processMessageQueue(): void {
    while (this.messageQueue.length > 0 && this.isConnected()) {
      const message = this.messageQueue.shift();
      if (message) {
        this.send(message);
      }
    }
  }

  send(message: WebSocketMessage): boolean {
    if (this.isConnected()) {
      try {
        // Add clientId to all outgoing messages
        const messageWithClientId = {
          ...message,
          clientId: this.clientId,
        };
        this.ws!.send(JSON.stringify(messageWithClientId));
        return true;
      } catch (error) {
        console.error(
          `Failed to send WebSocket message [${this.radarId}]:`,
          error
        );
        return false;
      }
    } else {
      const MAX_QUEUE_SIZE = 100;
      if (this.messageQueue.length >= MAX_QUEUE_SIZE) {
        this.messageQueue.shift();
      }
      this.messageQueue.push(message);
      return false;
    }
  }

  /**
   * Send a message in the server's expected format
   * Format: {"Type": "MessageType", "Payload": payload}
   */
  private sendServerMessage(type: string, payload: unknown): boolean {
    if (this.isConnected()) {
      try {
        const message = {
          Type: type,
          Payload: payload,
        };
        this.ws!.send(JSON.stringify(message));
        return true;
      } catch (error) {
        console.error(
          `Failed to send server message [${this.radarId}]:`,
          error
        );
        return false;
      }
    }
    return false;
  }

  addListener(listener: (event: WebSocketEvent) => void): () => void {
    this.listeners.push(listener);

    // Immediately notify the listener of the current connection state
    // This ensures the UI reflects the actual state even if events were missed
    const currentState = this.getConnectionState();

    if (currentState === "open") {
      listener({ type: "connected" });
    } else if (currentState === "closed" || currentState === "closing") {
      listener({ type: "disconnected" });
    }

    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  private notifyListeners(event: WebSocketEvent): void {
    this.listeners.forEach((listener) => listener(event));
  }

  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Start heartbeat monitoring to detect stale connections
   */
  private startHeartbeat(): void {
    this.stopHeartbeat(); // Clear any existing heartbeat

    this.lastPongTime = Date.now();

    // Send ping every 30 seconds. Not logged: a healthy tick isn't
    // information. Only the timeout branch below (a real problem) is
    // reported, to Sentry as well so it's visible on kiosks in the field.
    this.heartbeatInterval = setInterval(() => {
      if (this.isConnected()) {
        // Send ping in server's expected format: {"Type": "Ping", "Payload": null}
        this.sendServerMessage("Ping", null);

        // Set timeout to detect if pong doesn't arrive
        this.heartbeatTimeout = setTimeout(() => {
          const timeSinceLastPong = Date.now() - this.lastPongTime;
          if (timeSinceLastPong > this.HEARTBEAT_TIMEOUT) {
            captureException(
              new Error("WebSocket heartbeat timeout - forcing reconnection"),
              {
                type: "websocket-heartbeat-timeout",
                radarId: this.radarId,
                timeSinceLastPong,
              }
            );
            // Force reconnection by closing the connection
            // This will trigger onclose which will start the reconnection process
            if (this.ws) {
              try {
                this.ws.close();
              } catch (e) {
                console.error(
                  `Error closing stale WebSocket [${this.radarId}]:`,
                  e
                );
                // Manually trigger cleanup and reconnection if close fails
                this.ws = null;
                if (this.shouldBeConnected) {
                  this.attemptReconnect();
                }
              }
            }
          }
        }, this.HEARTBEAT_TIMEOUT);
      }
    }, this.HEARTBEAT_INTERVAL);
  }

  /**
   * Stop heartbeat monitoring
   */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout);
      this.heartbeatTimeout = null;
    }
  }

  disconnect(): void {
    console.log(`🔌 [WebSocket:${this.radarId}] Manually disconnecting`);
    this.shouldBeConnected = false; // Mark that we don't want to reconnect

    // Stop heartbeat monitoring
    this.stopHeartbeat();

    // Clear timers
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // Close connection
    if (this.ws) {
      try {
        this.ws.close();
      } catch (e) {
        console.error(`Error closing WebSocket [${this.radarId}]:`, e);
      }
      this.ws = null;
    }

    this.messageQueue = [];
    if (this.networkMonitorUnsubscribe) {
      this.networkMonitorUnsubscribe();
      this.networkMonitorUnsubscribe = null;
    }
    this.reconnectAttempts = 0;
    this.isConnecting = false;
    // Note: We don't reset hasHadSuccessfulConnection here as it's useful for debugging
  }

  getConnectionState(): WebSocketConnectionState {
    if (!this.ws) {
      return "closed";
    }

    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return "connecting";
      case WebSocket.OPEN:
        return "open";
      case WebSocket.CLOSING:
        return "closing";
      case WebSocket.CLOSED:
        return "closed";
      default:
        return "closed";
    }
  }

  getQueuedMessageCount(): number {
    return this.messageQueue.length;
  }

  /**
   * Refresh client ID and reconnect if needed
   * This should be called when client config changes
   */
  async refreshClientId(): Promise<void> {
    const clientConfig = await configManager.getConfig();
    const newClientId = clientConfig?.clientId || null;

    if (newClientId !== this.clientId) {
      console.log(
        `🔄 [WebSocket:${this.radarId}] Client ID changed, reconnecting...`,
        { old: this.clientId, new: newClientId }
      );

      this.clientId = newClientId;

      // If we're connected, reconnect to update the client ID
      if (this.isConnected()) {
        this.ws?.close(); // This will trigger reconnection
      }
    }
  }

  /**
   * Get detailed status for debugging
   */
  getDebugInfo(): {
    radarId: string;
    url: string;
    state: string;
    isConnected: boolean;
    shouldBeConnected: boolean;
    reconnectAttempts: number;
    queuedMessages: number;
    listenersCount: number;
    clientId: string | null;
    lastPongTime: number;
    hasHadSuccessfulConnection: boolean;
    isConnecting: boolean;
  } {
    return {
      radarId: this.radarId,
      url: this.url,
      state: this.getConnectionState(),
      isConnected: this.isConnected(),
      shouldBeConnected: this.shouldBeConnected,
      reconnectAttempts: this.reconnectAttempts,
      queuedMessages: this.messageQueue.length,
      listenersCount: this.listeners.length,
      clientId: this.clientId,
      lastPongTime: this.lastPongTime,
      hasHadSuccessfulConnection: this.hasHadSuccessfulConnection,
      isConnecting: this.isConnecting,
    };
  }
}
